import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { alphabetizeItems } from '../AlphabetListView';
import { renderRow } from '../Row';
import { CARRIER, TAB, LOCALIZED_STRINGS_FORM as LOCALIZED_STRINGS } from '../../../constants';
import { Spinner } from '../../../../Spinner';
import { PopularHeaderView } from './PopularHeaderView';
import { AllHeaderView } from './AllHeaderView';
import { handleFocus } from '../../HandleFocus';
import { LoadingContainer, SectionFooter, SectionList, SectionSeparator } from '../StyledDropdownComponents';
import { FLOW_SOURCE_ENUM } from '../../../data/dropdown-state/dropdownStateTracking';

class BaseItemsView extends Component {
    static propTypes = {
        active: PropTypes.bool,
        allItems: PropTypes.array.isRequired,
        isLoading: PropTypes.bool.isRequired,
        itemName: PropTypes.string.isRequired,
        popularItems: PropTypes.array.isRequired,
        showChooseLaterOption: PropTypes.bool,
        metricsStep: PropTypes.string.isRequired,
        metricsAction: PropTypes.string.isRequired,
        metricsDetailsIdField: PropTypes.string.isRequired,
    };

    static defaultProps = {
        active: false,
        showChooseLaterOption: false,
    };

    componentDidMount() {
        this.props.fetchPopular();
        this.props.fetchAll();
    }

    render() {
        const { isLoading } = this.props;

        if (isLoading) {
            return (
                <LoadingContainer data-test="loading-message">
                    <Spinner
                        inline
                        size={32}
                    />
                </LoadingContainer>
            );
        } else {
            return this.renderItems();
        }
    }

    renderItems() {
        const { focusState } = this.props;

        return (
            <SectionList innerRef={r => focusState.setScrollableArea(r)}>
                {this.maybeRenderNonInsuranceOptions()}
                {this.renderPopular()}
                {this.renderAll()}
                <SectionFooter />
            </SectionList>
        );
    }

    renderAll() {
        const {
            active,
            allItems,
            itemName,
            selected,
            clickHandler,
            tab,
            focusState,
            metricsAction,
            metricsStep,
            metricsDetailsIdField,
        } = this.props;

        if (!allItems.length) {
            return;
        }

        const alphabetizedItems = alphabetizeItems({
            active,
            clickHandler,
            focusState,
            items: allItems,
            selected,
            metricsAction,
            metricsStep,
            metricsDetailsIdField,
        });

        return (
            <div data-test={`all-${itemName}`}>
                <SectionFooter />
                <AllHeaderView tab={tab} />
                {alphabetizedItems.map(t => t)}
            </div>
        );
    }

    maybeRenderNonInsuranceOptions() {
        const { showChooseLaterOption } = this.props;

        if (!showChooseLaterOption) {
            // We will render I'm paying for myself as a popular option for this test
            return;
        }

        return (
            <div>
                {this.renderPayingMyself()}
                {this.renderIllChooseLater()}
                {this.renderNonInsuranceOptionsSeparator()}
            </div>
        );
    }

    maybeRenderPayingForMyself() {
        const { showChooseLaterOption } = this.props;

        if (showChooseLaterOption) {
            // if we are showing the choose later option, then we have already rendered this once
            return;
        }

        return this.renderPayingMyself();
    }

    renderPopular() {
        const {
            active,
            clickHandler,
            focusState,
            itemName,
            popularItems,
            selected,
            tab,
            metricsAction,
            metricsStep,
            metricsDetailsIdField,
        } = this.props;

        const topPopularItems = popularItems.slice(0, 10);

        return (
            <div data-test={`popular-${itemName}`}>
                <PopularHeaderView tab={tab} />
                <ol>
                    {topPopularItems.map(t =>
                        renderRow(t, selected, clickHandler, active && focusState, 'popular', {
                            action: metricsAction,
                            eventDetails: {
                                [metricsDetailsIdField]: t.id,
                                Name: t.name,
                                SelectionType: metricsStep,
                                Popular: true,
                            },
                        }))}
                    {this.maybeRenderPayingForMyself()}
                </ol>
            </div>
        );
    }

    renderNonInsuranceOptionsSeparator() {
        const { tab } = this.props;

        if (tab !== TAB.INSURANCE) {
            return;
        }

        return <SectionSeparator data-test="section-separator" />;
    }

    renderPayingMyself() {
        const {
            active,
            chooseNonInsuranceOption,
            focusState,
            intl,
            selected,
            tab,
            metricsAction,
            metricsStep,
        } = this.props;

        if (tab !== TAB.INSURANCE) {
            return;
        }

        const id = CARRIER.IM_PAYING_MYSELF;
        const name = intl.formatMessage(LOCALIZED_STRINGS.PAYING_MYSELF);

        return (
            <div data-test="paying-myself">
                {renderRow(
                    {
                        id,
                        name,
                        tag: 'div',
                    },
                    selected,
                    chooseNonInsuranceOption,
                    active && focusState,
                    null,
                    {
                        action: metricsAction,
                        eventDetails: {
                            InsuranceCarrierId: id,
                            Name: name,
                            SelectionType: metricsStep,
                        },
                    }
                )}
            </div>
        );
    }

    renderIllChooseLater() {
        const {
            active,
            chooseNonInsuranceOption,
            focusState,
            intl,
            selected,
            showChooseLaterOption,
            tab,
            metricsAction,
            metricsStep,
        } = this.props;

        if (tab !== TAB.INSURANCE || showChooseLaterOption === false) {
            return;
        }

        const clickHandler = item => chooseNonInsuranceOption(item, true, FLOW_SOURCE_ENUM.StepByStep);

        const id = CARRIER.ILL_CHOOSE_LATER;
        const name = intl.formatMessage(LOCALIZED_STRINGS.ILL_CHOOSE_LATER);

        return (
            <div data-test="choose-later">
                {renderRow(
                    {
                        id,
                        name,
                        tag: 'div',
                    },
                    selected,
                    clickHandler,
                    active && focusState,
                    null,
                    {
                        action: metricsAction,
                        eventDetails: {
                            InsuranceCarrierId: id,
                            Name: name,
                            SelectionType: metricsStep,
                        },
                    }
                )}
            </div>
        );
    }
}

export const ItemsView = injectIntl(
    handleFocus({
        shouldUpdate: (current, next) =>
            next.showAll !== current.showAll ||
            next.isLoading !== current.isLoading ||
            next.popularItems.length !== current.popularItems.length ||
            next.allItems.length !== current.allItems.length ||
            next.selected !== current.selected ||
            next.active !== current.active,
    })(BaseItemsView)
);
